import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { GraduationCap, ChevronDown, Lock, CalendarDays, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { LectureSignupForm } from './lecture/LectureSignupForm';

const API = process.env.REACT_APP_BACKEND_URL;

// Logged in: the user's lectures. Anonymous: public previews (used as sign-up magnet).
export const useLectures = () => {
  const { isAuthenticated, token } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    const req = isAuthenticated
      ? axios.get(`${API}/api/lectures`, { headers: { Authorization: `Bearer ${token}` } })
      : axios.get(`${API}/api/lectures/public`);
    req.then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [isAuthenticated, token]);
  return items;
};

export const LectureSignupDialog = ({ lecture, onClose, isItalian }) => (
  <Dialog open={!!lecture} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg" data-testid="lecture-signup-dialog">
      <DialogHeader>
        <DialogTitle className="text-2xl font-extrabold">{lecture?.title}</DialogTitle>
        <DialogDescription>{isItalian ? 'Registrati gratis per aprire la lecture, i materiali e le risorse.' : 'Sign up for free to open the lecture, its materials and resources.'}</DialogDescription>
      </DialogHeader>
      {lecture && <LectureSignupForm slug={lecture.slug} lang={isItalian ? 'it' : 'en'} onDone={onClose} />}
    </DialogContent>
  </Dialog>
);

const Item = ({ l, isItalian, authed, onPick }) => {
  const L = (o) => (isItalian ? o?.it : o?.en) || o?.en || '';
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="font-extrabold text-slate-900">{l.title}</p>
        {!authed && <Lock className="w-4 h-4 text-blue-600 shrink-0" />}
      </div>
      {l.subtitle && <p className="text-xs text-slate-600 mt-0.5">{L(l.subtitle)}</p>}
      <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {L(l.date)}{l.time ? ` · ${l.time}` : ''}</p>
      <p className="text-xs text-slate-500 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {L(l.venue)}</p>
      {!authed && <p className="mt-2 text-xs font-bold text-blue-700">{isItalian ? 'Registrati gratis per accedere →' : 'Sign up for free to access →'}</p>}
    </>
  );
  return authed
    ? <Link to={`/${l.slug}`} onClick={() => onPick(null)} className="block rounded-xl px-4 py-3 hover:bg-blue-50" data-testid={`navbar-lecture-${l.slug}`}>{body}</Link>
    : <button onClick={() => onPick(l)} className="w-full text-left rounded-xl px-4 py-3 hover:bg-blue-50" data-testid={`navbar-lecture-${l.slug}`}>{body}</button>;
};

export const LecturesMenu = ({ items, isItalian }) => {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [signup, setSignup] = useState(null);
  const pick = (l) => { setOpen(false); if (l) setSignup(l); };
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button onClick={() => setOpen(true)} className="flex items-center gap-1 text-slate-700 hover:text-blue-700 text-sm font-semibold tracking-wide" data-testid="navbar-lectures-link" aria-expanded={open}>
        <GraduationCap size={15} /> Lectures <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-3 w-80 z-50">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-xl p-2" data-testid="navbar-lectures-menu">
            {items.map((l) => <Item key={l.slug} l={l} isItalian={isItalian} authed={isAuthenticated} onPick={pick} />)}
            {!items.length && <p className="px-4 py-3 text-sm text-slate-500">{isItalian ? 'Nessuna lecture disponibile' : 'No lectures available'}</p>}
          </div>
        </div>
      )}
      <LectureSignupDialog lecture={signup} onClose={() => setSignup(null)} isItalian={isItalian} />
    </div>
  );
};
